// needs to be a separate file from Toolbar to avoid circular imports between Toolbar <-> SavedReplies

import {type CommandId, getPrimaryKeybinding, useCommandsContext} from '@github-ui/ui-commands'
import {getCommandMetadata} from '@github-ui/ui-commands/internal'
import {ActionBar, type IconButtonProps} from '@primer/react'
import {use} from 'react'

import {MarkdownEditorContext} from './MarkdownEditorContext'
import styles from './ToolbarButton.module.css'

type ToolbarButtonProps = IconButtonProps & {ref?: React.Ref<HTMLButtonElement>}

export const ToolbarButton = ({ref, ...props}: ToolbarButtonProps) => {
  const {disabled} = use(MarkdownEditorContext)

  return (
    <ActionBar.IconButton
      ref={ref}
      variant="invisible"
      disabled={disabled}
      // Prevent focus leaving input:
      onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
      {...props}
      className={styles.iconButton}
    />
  )
}
ToolbarButton.displayName = 'MarkdownEditor.ToolbarButton'

type CommandToolbarButtonProps = {
  commandId: CommandId
  icon: IconButtonProps['icon']
  ref?: React.Ref<HTMLButtonElement>
}

/**
 * A command-bound toolbar button. Like `CommandIconButton` from `@github-ui/ui-commands`, it derives its
 * `aria-label` and keybinding hint from the command metadata and triggers the command when clicked. Unlike
 * `CommandIconButton`, it renders through `ToolbarButton` (an `ActionBar.IconButton`) so that it continues to
 * participate in the toolbar's responsive overflow behavior.
 */
export const CommandToolbarButton = ({ref, commandId, icon}: CommandToolbarButtonProps) => {
  const metadata = getCommandMetadata(commandId)
  const {triggerCommand} = useCommandsContext()

  if (!metadata) return null

  return (
    <ToolbarButton
      ref={ref}
      aria-label={metadata.name}
      icon={icon}
      keybindingHint={getPrimaryKeybinding(commandId)}
      onClick={event => {
        if (!event.defaultPrevented) triggerCommand(commandId, event.nativeEvent)
      }}
    />
  )
}
CommandToolbarButton.displayName = 'MarkdownEditor.CommandToolbarButton'
